import React, { useState } from "react";
import { IconButton } from "@material-ui/core";
import { Edit } from "@material-ui/icons";
import { Pane, Text, CDialog, HFlex } from "shared";
import { useTranslation } from "react-i18next";
import { SPACING, Breakpoint } from "theme";
import { css } from "@emotion/css";
import { useStates } from "shared/ValueTable";
import { HealthCareOrganization, useCountries } from "../queries/useHealthCareOrganizations";
import { HealthCareOrganizationForm } from "./HealthCareOrganizationForm";

interface Props {
  hco: HealthCareOrganization;
}

const HcoInfo: React.VFC<Props> = ({ hco }) => {
  const { t } = useTranslation();
  const [isDialogOpen, setDialogOpen] = useState(false);
  const { data: states } = useStates();
  const { data: countries } = useCountries();

  const openDialog = () => setDialogOpen(true);
  const closeDialog = () => setDialogOpen(false);

  const platformFeeDisplay =
    hco.platform_fee != null && hco.platform_fee_unit
      ? `${hco.platform_fee} (${hco.platform_fee_unit})`
      : null;

  const countryName = hco.country_id != null
    ? countries?.find(c => c.id === hco.country_id)?.name ?? hco.country_id
    : null;

  const stateName = hco.us_state_id != null
    ? states?.find(s => s.id === hco.us_state_id)?.value ?? hco.us_state_id
    : null;

  const orgTypeLabels: Record<string, string> = { sme: "SME", enterprise: "Enterprise" };

  const details = [
    [t("form.name"), hco.name],
    [t("form.hco_contact_email"), hco.contact_email],
    [t("form.hco_billing_email"), hco.billing_email],
    [t("settings.profile_information.time_zone"), hco.iana_timezone],
    [t("form.hco_organization_type"), hco.organization_type ? orgTypeLabels[hco.organization_type] ?? hco.organization_type : null],
    [t("form.hco_platform_fee"), platformFeeDisplay],
    [t("form.hco_address"), hco.address],
    [t("form.hco_city"), hco.city],
    [t("form.hco_zip_code"), hco.zip_code],
    [t("form.hco_country"), countryName],
    [t("form.hco_state"), stateName],
    [t("form.hco_stripe_customer_id"), hco.stripe_customer_id],
    [t("form.hco_stripe_subscription_id"), hco.stripe_subscription_id],
    [t("form.hco_stripe_subscription_status"), hco.stripe_subscription_status],
    [t("form.created_at"), hco.created_at],
  ].filter(([, value]) => value != null && value !== "");

  return (
    <>
      <Pane dark>
        <HFlex mb={2} justifyContent="space-between" alignItems="center">
          <Text light>{t("form.hco_details")}</Text>
          <IconButton onClick={openDialog} size="small" aria-label={t("buttons.edit")}>
            <Edit fontSize="small" />
          </IconButton>
        </HFlex>
        <div className={styles.grid}>
          {details.map(([label, value]) => (
            <React.Fragment key={String(label)}>
              <Text light nowrap>
                {label}
              </Text>
              <Text bold nowrap>
                {value}
              </Text>
            </React.Fragment>
          ))}
        </div>
      </Pane>

      <CDialog maxWidth="sm" fullWidth open={isDialogOpen} onClose={closeDialog}>
        <HealthCareOrganizationForm organization={hco} onSuccess={closeDialog} onCancel={closeDialog} />
      </CDialog>
    </>
  );
};

export default HcoInfo;

const styles = {
  grid: css`
    overflow: auto;
    display: grid;
    grid-auto-rows: 45px;
    align-items: center;
    grid-column-gap: ${SPACING}px;
    @media screen and (max-width: ${Breakpoint.sm - 1}px) {
      grid-template-columns: 1fr;
    }
    @media screen and (min-width: ${Breakpoint.sm}px) {
      grid-template-columns: auto 1fr;
    }
  `,
};
